import React, { useEffect, useRef, useState } from 'react';
import './Catalog.css';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import CatalogFilter from './CatalogFilter';
import FavouriteButton from '../FavouriteButton/FavouriteButton';
import Footer from '../Footer/Footer';

const TEXT = {
    catalog: 'Каталог',
    all: 'Все',
    instock: 'В наличии',
    sort: 'Сортировка',
    sold: 'Распродано',
    sizeClarify: 'Размер уточняется',
    severalSizes: 'Несколько размеров',
    from: 'От',
    items: 'позиций',
    prev: 'Назад',
    next: 'Вперёд',
};

const SORT_LABELS = {
    new: 'Сначала новые',
    price_asc: 'Цена по возрастанию',
    price_desc: 'Цена по убыванию',
    popular: 'Популярные',
};

const getCollection = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (Array.isArray(value?.$values)) {
        return value.$values;
    }

    return [];
};

const normalizeStockMode = (value) => {
    const normalized = String(value || '').toLowerCase();

    if (normalized === 'preorder' || normalized === 'aproduct' || normalized === 'order') {
        return 'preorder';
    }

    if (
        normalized === 'instock' ||
        normalized === 'product' ||
        normalized === 'stock' ||
        normalized === 'inventory' ||
        normalized === '0'
    ) {
        return 'instock';
    }

    return 'all';
};

const normalizeCatalogKind = (catalogKind) => {
    if (catalogKind === 'all') {
        return 'all';
    }

    if (
        catalogKind === 'preorder' ||
        catalogKind === 'aproduct' ||
        catalogKind === 'order' ||
        catalogKind === 1 ||
        catalogKind === '1'
    ) {
        return 'preorder';
    }

    if (
        catalogKind === 'product' ||
        catalogKind === 'stock' ||
        catalogKind === 'inventory' ||
        catalogKind === 0 ||
        catalogKind === '0'
    ) {
        return 'instock';
    }

    return undefined;
};

const currencyFormatter = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const getCatalogContext = () => {
    const pathname = window.location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    const shoppingIndex = segments.indexOf('shopping');
    const shoppingSegments = shoppingIndex >= 0 ? segments.slice(shoppingIndex + 1) : [];
    const firstSegment = shoppingSegments[0];
    const isLegacyStockCatalog = firstSegment === 'instock';
    const hasCatalogPrefix = isLegacyStockCatalog || firstSegment === 'preorder' || firstSegment === 'aproduct';
    const categorySegments = (hasCatalogPrefix ? shoppingSegments.slice(1) : shoppingSegments).filter(
        (segment) => segment !== 'items'
    );
    const safeCategorySegments = categorySegments.length > 0 ? categorySegments : ['women'];
    const categoryPath = safeCategorySegments.join('/');
    const primarySection = safeCategorySegments[0];
    const stockMode = isLegacyStockCatalog
        ? 'instock'
        : normalizeStockMode(new URLSearchParams(window.location.search).get('stockMode'));

    return {
        categoryPath,
        primarySection,
        stockMode,
        catalogHref: `/shopping/${categoryPath}/items`,
    };
};

const getImageUrl = (item, index = 0) => {
    const image = getCollection(item?.ImagesPath)?.[index];
    return image?.URL || '';
};

const VISUAL_CATEGORIES = [
    { slug: 'clothing', title: 'Одежда' },
    { slug: 'shoes', title: 'Обувь' },
    { slug: 'bags', title: 'Сумки' },
    { slug: 'accessories', title: 'Аксессуары' },
    { slug: 'jewellery', title: 'Украшения' },
    { slug: 'watches', title: 'Часы' },
];

const getSortOptions = (data) => {
    const backendOptions = getCollection(data?.SortOptions);

    if (backendOptions.length > 0) {
        return backendOptions.map((option) => {
            if (typeof option === 'string') {
                return {
                    value: option,
                    label: SORT_LABELS[option] || option,
                };
            }

            return {
                value: option.Value,
                label: option.Label || SORT_LABELS[option.Value] || option.Value,
            };
        });
    }

    return ['new', 'price_asc', 'price_desc', 'popular'].map((value) => ({
        value,
        label: SORT_LABELS[value],
    }));
};

const normalizeItemUrl = (url) => {
    if (!url) {
        return '';
    }

    if (/^https?:\/\//i.test(url)) {
        return url;
    }

    if (url.startsWith('/')) {
        return url;
    }

    if (url.startsWith('./')) {
        return `/${url.slice(2)}`;
    }

    return `/${url}`;
};

const getCardHref = (item, catalogContext, isPreorderItem) => {
    if (item?.Url) {
        if (!/^https?:\/\//i.test(item.Url) && !item.Url.startsWith('/') && !item.Url.startsWith('./')) {
            return isPreorderItem
                ? `/shopping/preorder/${catalogContext.primarySection}/${item.Url}`
                : `/shopping/product/${catalogContext.primarySection}/${item.Url}`;
        }

        return normalizeItemUrl(item.Url);
    }

    if (!item?.Sename) {
        return '#';
    }

    if (/^https?:\/\//i.test(item.Sename) || item.Sename.startsWith('/')) {
        return item.Sename;
    }

    return isPreorderItem
        ? `/shopping/preorder/${catalogContext.primarySection}/${item.Sename}`
        : `/shopping/product/${catalogContext.primarySection}/${item.Sename}`;
};

const getPagination = (data) => {
    const pagination = data?.Pagination || {};
    const totalPages = Number(pagination.TotalPages) || 1;
    const currentPage = Number(pagination.CurrentPage) || 1;
    const pageSize = Number(pagination.PageSize) || 24;
    const totalCount = Number(pagination.TotalCount) || Number(data?.Amount) || 0;

    return {
        currentPage: Math.min(Math.max(currentPage, 1), totalPages),
        pageSize,
        totalCount,
        totalPages,
    };
};

const isPreorderProduct = (item) => {
    const productKind = String(item?.ProductKindName || item?.ProductKind || '').toLowerCase();
    return item?.IsForOrder === true || productKind === 'aproduct' || productKind === '1';
};

const Catalog = ({ data }) => {
    const catalogContext = getCatalogContext();
    const products = getCollection(data?.ProductList).length > 0 ? getCollection(data?.ProductList) : getCollection(data?.Products);
    const currentTab = data?.Tab || null;
    const sortOptions = getSortOptions(data);
    const initialSort = data?.Sort || sortOptions[0]?.value || 'new';
    const pagination = getPagination(data);
    const productCount = pagination.totalCount || products.length;
    const [currentSort, setCurrentSort] = useState(initialSort);
    const [visibleProducts, setVisibleProducts] = useState(products);
    const [visiblePageSize, setVisiblePageSize] = useState(pagination.pageSize);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [cardImageIndexes, setCardImageIndexes] = useState({});
    const [hoveredCardKey, setHoveredCardKey] = useState(null);
    const [interactedCards, setInteractedCards] = useState({});
    const [returningCards, setReturningCards] = useState({});
    const touchGestureRef = useRef(null);
    const swipedCardRef = useRef(null);
    const cardMediaElementsRef = useRef(new Map());
    const galleryResetTimersRef = useRef(new Map());
    const desktopFadeTimersRef = useRef(new Map());

    const handleCatalogSort = (sortValue) => {
        setCurrentSort(sortValue || initialSort);
        setVisibleProducts((current) => {
            if (sortValue !== 'price_asc' && sortValue !== 'price_desc') return [...products];
            return [...current].sort((first, second) => {
                const difference = Number(first?.Price || 0) - Number(second?.Price || 0);
                return sortValue === 'price_asc' ? difference : -difference;
            });
        });
    };

    useEffect(() => {
        const isTouchLayout = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        if (!isTouchLayout || typeof IntersectionObserver === 'undefined') return undefined;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) return;
                const cardKey = entry.target.dataset.cardKey;
                if (!cardKey) return;

                const pendingTimer = galleryResetTimersRef.current.get(cardKey);
                if (pendingTimer) window.clearTimeout(pendingTimer);
                galleryResetTimersRef.current.delete(cardKey);

                setCardImageIndexes((current) => current[cardKey]
                    ? { ...current, [cardKey]: 0 }
                    : current);
                setInteractedCards((current) => current[cardKey]
                    ? { ...current, [cardKey]: false }
                    : current);
            });
        }, { threshold: 0.05 });

        cardMediaElementsRef.current.forEach((element) => observer.observe(element));
        return () => observer.disconnect();
    }, [visibleProducts]);

    useEffect(() => () => {
        galleryResetTimersRef.current.forEach((timer) => window.clearTimeout(timer));
        galleryResetTimersRef.current.clear();
        desktopFadeTimersRef.current.forEach((timer) => window.clearTimeout(timer));
        desktopFadeTimersRef.current.clear();
    }, []);

    useEffect(() => {
        const isTouchLayout = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        if (!isTouchLayout) return undefined;

        let previousScrollY = window.scrollY;
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY < previousScrollY) {
                galleryResetTimersRef.current.forEach((timer) => window.clearTimeout(timer));
                galleryResetTimersRef.current.clear();
                setCardImageIndexes((current) => {
                    const hasChangedImage = Object.values(current).some((index) => index !== 0);
                    if (!hasChangedImage) return current;
                    return Object.keys(current).reduce((next, key) => ({ ...next, [key]: 0 }), {});
                });
                setInteractedCards((current) => Object.keys(current).length ? {} : current);
                setHoveredCardKey(null);
            }
            previousScrollY = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    const heroTitle = data?.Title || currentTab?.Title || TEXT.catalog;
    const stockMode = normalizeStockMode(data?.StockMode || normalizeCatalogKind(data?.CatalogKind) || catalogContext.stockMode);
    const catalogKind = stockMode === 'instock' ? 'stock' : stockMode;
    const handleLoadMore = async () => {
        if (isLoadingMore || visibleProducts.length >= productCount) {
            return;
        }

        const nextPageSize = Math.min(visiblePageSize + 24, productCount);
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.delete('page');
        requestUrl.searchParams.set('pageSize', String(nextPageSize));
        requestUrl.searchParams.set('partial', '1');
        setIsLoadingMore(true);

        try {
            const response = await fetch(`${requestUrl.pathname}${requestUrl.search}`, {
                headers: { Accept: 'application/json' },
            });

            if (!response.ok) {
                throw new Error(`Catalog request failed: ${response.status}`);
            }

            const nextData = await response.json();
            const nextProducts = getCollection(nextData?.ProductList);
            setVisibleProducts(nextProducts);
            setVisiblePageSize(Number(nextData?.Pagination?.PageSize) || nextPageSize);

            requestUrl.searchParams.delete('partial');
            window.history.replaceState({}, '', `${requestUrl.pathname}${requestUrl.search}`);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const isMainCatalog = catalogContext.categoryPath.split('/').length === 1;
    const visualCategories = VISUAL_CATEGORIES.map((category, index) => {
        const matchingProduct = products.find((product) => product?.CategorySlug === category.slug);
        const fallbackProduct = products[index % Math.max(products.length, 1)];

        return {
            ...category,
            href: `/shopping/${catalogContext.primarySection}/${category.slug}/items`,
            image: getImageUrl(matchingProduct || fallbackProduct),
        };
    });

    return (
        <>
            <section className="catalog-page">
                <div className="catalog-shell">
                    {currentTab?.Parent && <Breadcrumbs category={currentTab} catalogKind={catalogKind} />}

                    <div className="catalog-hero">
                        <div className="catalog-hero__content">
                            <h1 className="catalog-hero__title">{heroTitle}</h1>
                        </div>
                    </div>

                    {isMainCatalog && (
                        <nav className="catalog-visual-nav" aria-label="Категории каталога">
                            {visualCategories.map((category) => (
                                <a className="catalog-visual-nav__item" href={category.href} key={category.slug}>
                                    <span className="catalog-visual-nav__image-wrap">
                                        {category.image && <img src={category.image} alt="" className="catalog-visual-nav__image" />}
                                    </span>
                                    <span className="catalog-visual-nav__title">{category.title}</span>
                                </a>
                            ))}
                        </nav>
                    )}

                    <div className="catalog-toolbar">
                        <div className="catalog-toolbar__left">
                            <CatalogFilter filters={data?.Filters} sortValue={currentSort} onSortChange={handleCatalogSort} />
                        </div>
                        <span className="catalog-toolbar__count">{productCount} {TEXT.items}</span>
                    </div>

                    <ul className="catalog-grid">
                        {visibleProducts.map((item) => {
                            const isPreorderItem = isPreorderProduct(item);
                            const cardHref = getCardHref(item, catalogContext, isPreorderItem);
                            const galleryImages = getCollection(item?.ImagesPath).map((image) => image?.URL).filter(Boolean);
                            const mobileGalleryImages = galleryImages.slice(0, 5);
                            const isTouchLayout = typeof window !== 'undefined' && window.matchMedia('(hover: none), (pointer: coarse)').matches;
                            const cardKey = `${isPreorderItem ? 'aproduct' : 'product'}:${item?.ProductId || item?.Sename || item?.$id}`;
                            const currentImageIndex = Math.min(cardImageIndexes[cardKey] || 0, Math.max(galleryImages.length - 1, 0));
                            const isAutoHovering = hoveredCardKey === cardKey && !interactedCards[cardKey] && galleryImages.length > 1;
                            const displayedImageIndex = isTouchLayout
                                ? Math.min(currentImageIndex, Math.max(mobileGalleryImages.length - 1, 0))
                                : (isAutoHovering ? 1 : currentImageIndex);
                            const sizes = item?.SizeStr ? [item.SizeStr] : [];
                            const sizeLabel = isPreorderItem
                                ? ''
                                : sizes.length > 3
                                    ? TEXT.severalSizes
                                    : sizes.join(', ');
                            const priceLabel = !isPreorderItem && sizes.length > 1
                                ? `${TEXT.from} ${formatCurrency(item?.Price)}`
                                : formatCurrency(item?.Price);
                            const oldPriceLabel = item?.OldPrice ? formatCurrency(item.OldPrice) : null;
                            const isAvailable = item?.IsAvailable !== false;
                            const showFavourite = !isPreorderItem;

                            return (
                                <li
                                    key={cardKey}
                                    className={`catalog-card ${isAutoHovering ? 'is-hover-preview' : ''} ${returningCards[cardKey] ? 'is-returning' : ''}`}
                                >
                                    {showFavourite && (
                                        <div className="catalog-card__favorite">
                                            <FavouriteButton productId={item.ProductId} />
                                        </div>
                                    )}

                                    <div
                                        className="catalog-card__image-frame"
                                        data-card-key={cardKey}
                                        ref={(element) => {
                                            if (element) cardMediaElementsRef.current.set(cardKey, element);
                                            else cardMediaElementsRef.current.delete(cardKey);
                                        }}
                                        onPointerEnter={(event) => {
                                            const supportsRealHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
                                            if (event.pointerType === 'mouse' && supportsRealHover) {
                                                const pendingFade = desktopFadeTimersRef.current.get(cardKey);
                                                if (pendingFade) window.clearTimeout(pendingFade);
                                                desktopFadeTimersRef.current.delete(cardKey);
                                                setReturningCards((current) => ({ ...current, [cardKey]: false }));
                                                setHoveredCardKey(cardKey);
                                            }
                                        }}
                                        onPointerLeave={(event) => {
                                            const supportsRealHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
                                            if (event.pointerType !== 'mouse' || !supportsRealHover) return;
                                            const focusedControl = event.currentTarget.querySelector(':focus');
                                            if (focusedControl instanceof HTMLElement) focusedControl.blur();
                                            setReturningCards((current) => ({ ...current, [cardKey]: true }));
                                            setHoveredCardKey(null);
                                            setCardImageIndexes((current) => ({ ...current, [cardKey]: 0 }));
                                            setInteractedCards((current) => ({ ...current, [cardKey]: false }));
                                            const pendingFade = desktopFadeTimersRef.current.get(cardKey);
                                            if (pendingFade) window.clearTimeout(pendingFade);
                                            desktopFadeTimersRef.current.set(cardKey, window.setTimeout(() => {
                                                setReturningCards((current) => ({ ...current, [cardKey]: false }));
                                                desktopFadeTimersRef.current.delete(cardKey);
                                            }, 760));
                                        }}
                                        onTouchStart={(event) => {
                                            touchGestureRef.current = {
                                                cardKey,
                                                x: event.touches[0]?.clientX || 0,
                                                y: event.touches[0]?.clientY || 0,
                                            };
                                        }}
                                        onTouchEnd={(event) => {
                                            const gesture = touchGestureRef.current;
                                            const touch = event.changedTouches[0];
                                            touchGestureRef.current = null;

                                            if (!gesture || gesture.cardKey !== cardKey || !touch || mobileGalleryImages.length < 2) return;

                                            const deltaX = touch.clientX - gesture.x;
                                            const deltaY = touch.clientY - gesture.y;
                                            const swipeThreshold = Math.max(14, Math.min(22, event.currentTarget.clientWidth * 0.08));
                                            if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY) * 0.75) return;

                                            swipedCardRef.current = cardKey;
                                            setInteractedCards((current) => ({ ...current, [cardKey]: true }));
                                            setCardImageIndexes((current) => ({
                                                ...current,
                                                [cardKey]: deltaX < 0
                                                    ? (currentImageIndex + 1) % mobileGalleryImages.length
                                                    : (currentImageIndex - 1 + mobileGalleryImages.length) % mobileGalleryImages.length,
                                            }));

                                            const pendingTimer = galleryResetTimersRef.current.get(cardKey);
                                            if (pendingTimer) window.clearTimeout(pendingTimer);
                                            galleryResetTimersRef.current.set(cardKey, window.setTimeout(() => {
                                                setCardImageIndexes((current) => ({ ...current, [cardKey]: 0 }));
                                                setInteractedCards((current) => ({ ...current, [cardKey]: false }));
                                                galleryResetTimersRef.current.delete(cardKey);
                                            }, 4500));
                                        }}
                                    >
                                        <a
                                            href={cardHref}
                                            className="catalog-card__image-link"
                                            aria-label={item?.Title}
                                            onClick={(event) => {
                                                if (swipedCardRef.current !== cardKey) return;
                                                event.preventDefault();
                                                swipedCardRef.current = null;
                                            }}
                                        >
                                            <div className="catalog-card__image-wrap">
                                                <div
                                                    className="catalog-card__image-track"
                                                    style={{ transform: `translate3d(-${displayedImageIndex * 100}%, 0, 0)` }}
                                                >
                                                    {galleryImages.map((imageUrl, imageIndex) => (
                                                        <img
                                                            src={imageUrl}
                                                            alt={imageIndex === 0 ? item?.Title : ''}
                                                            aria-hidden={imageIndex === 0 ? undefined : 'true'}
                                                            className={`catalog-card__image ${imageIndex === displayedImageIndex ? 'is-active' : ''} ${!isAvailable ? 'is-muted' : ''}`}
                                                            height="480"
                                                            loading={imageIndex === 0 ? undefined : 'lazy'}
                                                            width="360"
                                                            key={imageUrl}
                                                />
                                                    ))}
                                                </div>
                                                {galleryImages.length > 1 && (
                                                    <div
                                                        className="catalog-card__image-progress"
                                                        aria-label={`Фото ${displayedImageIndex + 1} из ${mobileGalleryImages.length}`}
                                                    >
                                                        {mobileGalleryImages.map((imageUrl, imageIndex) => (
                                                            <span
                                                                key={`${imageUrl}-progress`}
                                                                className={imageIndex === displayedImageIndex ? 'is-active' : ''}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                                {!isAvailable && <span className="catalog-card__badge">{TEXT.sold}</span>}
                                            </div>
                                        </a>

                                        {galleryImages.length > 1 && (
                                            <div className="catalog-card__gallery-nav" aria-label="Фотографии товара">
                                                <button
                                                    type="button"
                                                    className="catalog-card__gallery-arrow catalog-card__gallery-arrow--prev"
                                                    aria-label="Предыдущее фото"
                                                    onClick={() => {
                                                        setInteractedCards((current) => ({ ...current, [cardKey]: true }));
                                                        setCardImageIndexes((current) => ({
                                                            ...current,
                                                            [cardKey]: (displayedImageIndex - 1 + galleryImages.length) % galleryImages.length,
                                                        }));
                                                    }}
                                                >‹</button>
                                                <button
                                                    type="button"
                                                    className="catalog-card__gallery-arrow catalog-card__gallery-arrow--next"
                                                    aria-label="Следующее фото"
                                                    onClick={() => {
                                                        setInteractedCards((current) => ({ ...current, [cardKey]: true }));
                                                        setCardImageIndexes((current) => ({
                                                            ...current,
                                                            [cardKey]: (displayedImageIndex + 1) % galleryImages.length,
                                                        }));
                                                    }}
                                                >›</button>
                                            </div>
                                        )}
                                    </div>

                                    <a href={cardHref} className="catalog-card__link">
                                        <div className="catalog-card__body">
                                            <div className="catalog-card__brand">{item?.BrandName}</div>
                                            <div className="catalog-card__title">{item?.Title}</div>
                                            {/* <div className="catalog-card__details">{sizeLabel || TEXT.sizeClarify}</div> */}
                                            <div className="catalog-card__price-row">
                                                {oldPriceLabel && (
                                                    <span className="catalog-card__price catalog-card__price--old">
                                                        {oldPriceLabel}
                                                    </span>
                                                )}
                                                <span className="catalog-card__price">{priceLabel}</span>
                                            </div>
                                        </div>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>

                    {visibleProducts.length > 0 && (
                        <div className="catalog-load-more">
                            <div className="catalog-load-more__count">
                                1–{visibleProducts.length} из {productCount} товаров
                            </div>
                            {visibleProducts.length < productCount && (
                                <button
                                    className="catalog-load-more__button"
                                    type="button"
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? 'Загрузка…' : 'Показать ещё'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </>
    );
};

export default Catalog;
